// =====================================================================
//  ADMIN CONSOLE, HELPDESK
// ---------------------------------------------------------------------
//  The org-wide ticket queue.
//
//  The employee portal only ever showed a person their own tickets, which
//  meant nobody had a view of the queue they were supposed to be working.
//  This is that view: every ticket raised in the organization, triaged by
//  status and priority, assignable, and resolvable from the row.
// =====================================================================

import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Check,
  CheckCheck,
  Clock,
  Download,
  Inbox,
  LifeBuoy,
  MoreHorizontal,
  UserCheck,
} from 'lucide-react';

import backendApi from '../../api/backendApi';
import DataTable from '../DataTable';
import {
  Button,
  Chip,
  Drawer,
  Field,
  FilterChip,
  Grid,
  Input,
  KeyValue,
  KpiTile,
  Menu,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  PageHeader,
  PersonCell,
  SearchInput,
  StatusChip,
  Tabs,
  Toolbar,
  ToolbarSpacer,
  toast,
  useDebounced,
} from '../ui';
import { DASH, distinct, downloadCsv, fmtDateTime, fmtNumber, matchesSearch, timeAgo, toISODate } from '../format';

const STATUSES = ['Open', 'In Progress', 'Resolved', 'Closed'];
const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];

const priorityTone = (priority) => {
  switch (String(priority || '').toLowerCase()) {
    case 'urgent':
    case 'critical':
      return 'negative';
    case 'high':
      return 'warning';
    case 'low':
      return 'neutral';
    default:
      return 'info';
  }
};

export default function HelpdeskPage({ role, currentUser }) {
  const queryClient = useQueryClient();
  const canManage = ['admin', 'hr', 'manager'].includes(role);

  const [tab, setTab] = useState('open');
  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [active, setActive] = useState(null);
  const [assignee, setAssignee] = useState('');
  const [selected, setSelected] = useState([]);

  const debouncedSearch = useDebounced(search, 250);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'tickets'],
    queryFn: () => backendApi.entities.Ticket.page({ limit: 500, sort: 'created_at', order: 'desc' }),
  });

  // Pinned identity so the bucket/filter memos don't recompute every render.
  const tickets = useMemo(() => data?.rows || [], [data]);

  /* ------------------------------ mutations --------------------------- */
  const update = useMutation({
    mutationFn: ({ id, patch }) => backendApi.entities.Ticket.update(id, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (err) => toast.error(err.message || 'Could not update this ticket'),
  });

  const setStatus = (id, status) =>
    update.mutate({ id, patch: { status } }, { onSuccess: () => toast.success(`Ticket marked ${status}`) });

  const bulkResolve = useMutation({
    mutationFn: async (ids) => {
      const done = [];
      for (const id of ids) {
        // eslint-disable-next-line no-await-in-loop
        done.push(await backendApi.entities.Ticket.update(id, { status: 'Resolved' }));
      }
      return done;
    },
    onSuccess: (done) => {
      toast.success(`${done.length} ticket${done.length === 1 ? '' : 's'} resolved`);
      setSelected([]);
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (err) => toast.error(err.message || 'Bulk update failed'),
  });

  const assign = () => {
    if (!active) return;
    update.mutate(
      { id: active.id, patch: { assigned_to: assignee, status: active.status === 'Open' ? 'In Progress' : active.status } },
      {
        onSuccess: () => {
          toast.success(assignee ? `Assigned to ${assignee}` : 'Assignee cleared');
          setActive((prev) => (prev ? { ...prev, assigned_to: assignee } : prev));
        },
      }
    );
  };

  /* --------------------------- derived views -------------------------- */
  const buckets = useMemo(() => {
    const isOpen = (t) => /^open$/i.test(t.status || '');
    const isProgress = (t) => /in\s?_?progress/i.test(t.status || '');
    const isDone = (t) => /resolved|closed/i.test(t.status || '');
    return {
      open: tickets.filter(isOpen),
      progress: tickets.filter(isProgress),
      done: tickets.filter(isDone),
      unassigned: tickets.filter((t) => !t.assigned_to && !isDone(t)),
    };
  }, [tickets]);

  const source =
    tab === 'open' ? buckets.open : tab === 'progress' ? buckets.progress : tab === 'done' ? buckets.done : tickets;

  const categories = useMemo(() => distinct(tickets, 'category'), [tickets]);

  const filtered = useMemo(
    () =>
      source.filter(
        (row) =>
          matchesSearch(row, debouncedSearch, ['title', 'description', 'raised_by_name', 'assigned_to', 'category']) &&
          (!priorityFilter || String(row.priority).toLowerCase() === priorityFilter.toLowerCase()) &&
          (!categoryFilter || row.category === categoryFilter)
      ),
    [source, debouncedSearch, priorityFilter, categoryFilter]
  );

  const highPriorityOpen = buckets.open.filter((t) => /high|urgent|critical/i.test(t.priority || '')).length;

  const columns = useMemo(
    () => [
      {
        key: 'title',
        label: 'Ticket',
        width: 280,
        render: (row) => (
          <div className="ac-cell-stack">
            <span className="ac-cell-main">{row.title || 'Untitled'}</span>
            <span className="ac-cell-sub">
              #{row.id} · {row.category || 'General'}
            </span>
          </div>
        ),
      },
      {
        key: 'priority',
        label: 'Priority',
        width: 100,
        render: (row) => (row.priority ? <Chip tone={priorityTone(row.priority)}>{row.priority}</Chip> : DASH),
        sortValue: (row) => PRIORITIES.indexOf(row.priority),
      },
      { key: 'status', label: 'Status', width: 120, render: (row) => <StatusChip value={row.status} /> },
      {
        key: 'raised_by_name',
        label: 'Raised by',
        width: 180,
        render: (row) => (row.raised_by_name ? <PersonCell name={row.raised_by_name} /> : DASH),
      },
      {
        key: 'assigned_to',
        label: 'Assigned to',
        width: 150,
        render: (row) =>
          row.assigned_to ? (
            <span>{row.assigned_to}</span>
          ) : (
            <Chip tone="warning">Unassigned</Chip>
          ),
      },
      {
        key: 'created_at',
        label: 'Raised',
        width: 110,
        render: (row) => <span title={fmtDateTime(row.created_at)}>{timeAgo(row.created_at)}</span>,
        csv: (row) => row.created_at,
      },
      {
        key: 'updated_at',
        label: 'Updated',
        width: 110,
        defaultHidden: true,
        render: (row) => timeAgo(row.updated_at),
        csv: (row) => row.updated_at,
      },
    ],
    []
  );

  const exportCsv = () => {
    downloadCsv(`tickets-${toISODate()}`, filtered, [
      { key: 'id', label: 'ID' },
      { key: 'title', label: 'Title' },
      { key: 'category', label: 'Category' },
      { key: 'priority', label: 'Priority' },
      { key: 'status', label: 'Status' },
      { key: 'raised_by_name', label: 'Raised by' },
      { key: 'assigned_to', label: 'Assigned to' },
      { key: 'description', label: 'Description' },
      { key: 'created_at', label: 'Raised at' },
    ]);
    toast.success(`Exported ${filtered.length} tickets`);
  };

  return (
    <div className="ac-page ac-page--wide">
      <PageHeader
        title="Helpdesk"
        sub="Every request raised by your employees, in one queue."
        actions={
          <Button icon={Download} onClick={exportCsv} disabled={filtered.length === 0}>
            Export
          </Button>
        }
      />

      <Grid cols={4} style={{ marginBottom: 14 }}>
        <KpiTile
          label="Open"
          value={fmtNumber(buckets.open.length)}
          icon={Inbox}
          tone={buckets.open.length > 0 ? 'warn' : 'pos'}
          foot={buckets.open.length === 0 ? 'Queue is clear' : 'Not yet picked up'}
        />
        <KpiTile
          label="In progress"
          value={fmtNumber(buckets.progress.length)}
          icon={Clock}
          foot="Being worked on"
        />
        <KpiTile
          label="High priority"
          value={fmtNumber(highPriorityOpen)}
          icon={AlertTriangle}
          tone={highPriorityOpen > 0 ? 'neg' : 'pos'}
          foot="Open and urgent"
        />
        <KpiTile
          label="Unassigned"
          value={fmtNumber(buckets.unassigned.length)}
          icon={UserCheck}
          tone={buckets.unassigned.length > 0 ? 'warn' : 'pos'}
          foot="Nobody owns these yet"
        />
      </Grid>

      <Tabs
        items={[
          { key: 'open', label: 'Open', count: buckets.open.length },
          { key: 'progress', label: 'In progress', count: buckets.progress.length },
          { key: 'done', label: 'Resolved', count: buckets.done.length },
          { key: 'all', label: 'All', count: tickets.length },
        ]}
        value={tab}
        onChange={(next) => {
          setTab(next);
          setSelected([]);
        }}
      />

      <div style={{ marginTop: 14 }}>
        <DataTable
          columns={columns}
          rows={filtered}
          loading={isLoading}
          onRowClick={(row) => {
            setActive(row);
            setAssignee(row.assigned_to || '');
          }}
          selectable={canManage && tab !== 'done'}
          selected={selected}
          onSelectedChange={setSelected}
          defaultSort={{ key: 'created_at', dir: 'desc' }}
          emptyIcon={tab === 'open' ? CheckCheck : LifeBuoy}
          emptyTitle={
            tab === 'open' ? 'No open tickets' : tickets.length === 0 ? 'No tickets yet' : 'No matching tickets'
          }
          emptyText={
            tab === 'open'
              ? 'Everything raised has been picked up or resolved.'
              : tickets.length === 0
              ? 'Requests raised from the employee portal will appear here.'
              : 'Adjust the search or filters above.'
          }
          bulkActions={(ids) => (
            <Button size="sm" icon={Check} onClick={() => bulkResolve.mutate(ids)} loading={bulkResolve.isPending}>
              Mark resolved
            </Button>
          )}
          rowActions={
            canManage
              ? (row) => (
                  <Menu
                    align="right"
                    trigger={({ toggle }) => (
                      <button
                        type="button"
                        className="ac-iconbtn"
                        onClick={toggle}
                        aria-label="Row actions"
                        style={{ width: 24, height: 24 }}
                      >
                        <MoreHorizontal size={14} strokeWidth={2} />
                      </button>
                    )}
                  >
                    <MenuLabel>Set status</MenuLabel>
                    {STATUSES.map((status) => (
                      <MenuItem key={status} onClick={() => setStatus(row.id, status)}>
                        {status}
                      </MenuItem>
                    ))}
                    <MenuSeparator />
                    <MenuItem
                      icon={UserCheck}
                      onClick={() =>
                        update.mutate(
                          {
                            id: row.id,
                            patch: {
                              status: 'In Progress',
                              assigned_to: currentUser?.full_name || currentUser?.email,
                            },
                          },
                          { onSuccess: () => toast.success('Assigned to you') }
                        )
                      }
                    >
                      Assign to me
                    </MenuItem>
                  </Menu>
                )
              : undefined
          }
          toolbar={
            <Toolbar>
              <SearchInput value={search} onChange={setSearch} placeholder="Search title, reporter…" width={240} />
              <FilterChip label="Priority" value={priorityFilter} options={PRIORITIES} onChange={setPriorityFilter} />
              <FilterChip label="Category" value={categoryFilter} options={categories} onChange={setCategoryFilter} />
              <ToolbarSpacer />
              <span style={{ fontSize: 11.5, color: 'var(--ac-faint)' }}>{fmtNumber(filtered.length)} tickets</span>
            </Toolbar>
          }
        />
      </div>

      {/* --------------------------- DRAWER --------------------------- */}
      <Drawer
        open={Boolean(active)}
        onClose={() => setActive(null)}
        title={active?.title || 'Ticket'}
        sub={active ? `#${active.id} · raised by ${active.raised_by_name || 'employee'}` : null}
        badge={
          active ? (
            <span style={{ display: 'inline-flex', gap: 5 }}>
              <StatusChip value={active.status} />
              {active.priority ? <Chip tone={priorityTone(active.priority)}>{active.priority}</Chip> : null}
            </span>
          ) : null
        }
        footer={
          canManage && active ? (
            <>
              <div style={{ flex: '1 1 auto' }} />
              <Button onClick={() => setActive(null)}>Close</Button>
              {!/resolved|closed/i.test(active.status || '') ? (
                <Button
                  variant="accent"
                  icon={Check}
                  loading={update.isPending}
                  onClick={() => {
                    setStatus(active.id, 'Resolved');
                    setActive(null);
                  }}
                >
                  Mark resolved
                </Button>
              ) : null}
            </>
          ) : (
            <Button onClick={() => setActive(null)}>Close</Button>
          )
        }
      >
        {active ? (
          <>
            <div className="ac-eyebrow" style={{ marginBottom: 7 }}>
              Details
            </div>
            <KeyValue
              items={[
                { key: 'Category', value: active.category ? <Chip>{active.category}</Chip> : null },
                { key: 'Priority', value: active.priority ? <Chip tone={priorityTone(active.priority)}>{active.priority}</Chip> : null },
                { key: 'Status', value: <StatusChip value={active.status} /> },
                { key: 'Raised by', value: active.raised_by_name },
                { key: 'Raised at', value: fmtDateTime(active.created_at) },
                { key: 'Last updated', value: fmtDateTime(active.updated_at) },
              ]}
            />

            {active.description ? (
              <>
                <div className="ac-eyebrow" style={{ margin: '20px 0 7px' }}>
                  Description
                </div>
                <div
                  style={{
                    fontSize: 12.5,
                    lineHeight: 1.6,
                    color: 'var(--ac-body)',
                    whiteSpace: 'pre-wrap',
                    background: 'var(--ac-sunken)',
                    border: '1px solid var(--ac-line-soft)',
                    borderRadius: 'var(--ac-r)',
                    padding: 12,
                  }}
                >
                  {active.description}
                </div>
              </>
            ) : null}

            {canManage ? (
              <>
                <div className="ac-eyebrow" style={{ margin: '20px 0 7px' }}>
                  Assignment
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <Field label="Assigned to" style={{ flex: '1 1 auto' }}>
                    <Input
                      value={assignee}
                      onChange={(event) => setAssignee(event.target.value)}
                      placeholder="Name of the owner"
                    />
                  </Field>
                  <Button onClick={assign} loading={update.isPending}>
                    Save
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setAssignee(currentUser?.full_name || '');
                    }}
                  >
                    Assign to me
                  </Button>
                </div>

                <div className="ac-eyebrow" style={{ margin: '20px 0 7px' }}>
                  Change status
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {STATUSES.map((status) => (
                    <Button
                      key={status}
                      size="sm"
                      variant={String(active.status).toLowerCase() === status.toLowerCase() ? 'primary' : 'default'}
                      onClick={() => {
                        setStatus(active.id, status);
                        setActive((prev) => (prev ? { ...prev, status } : prev));
                      }}
                    >
                      {status}
                    </Button>
                  ))}
                </div>
              </>
            ) : null}
          </>
        ) : null}
      </Drawer>
    </div>
  );
}
